/**
 * Horaires et contact du magasin — sur l'entrée de la boutique.
 *
 * « L'horaire du magasin n'est pas visible » (Philippe, 28/09/2026) : il ne vivait
 * qu'au pied de page, sous 1 000 produits. Or le client retire sa commande au
 * magasin : quand venir est la question qui suit « quoi commander ».
 *
 * Lu dans les réglages magasin (`/shop-config` → `store.opening_hours`), jamais
 * écrit en dur. Le jour d'aujourd'hui est mis en avant ; le dimanche au marché
 * se distingue (autre lieu) ; les jours fermés sont dits en une ligne.
 * « De même que le contact » : téléphone et email, touchables (appel, courriel).
 * L'email est celui du pied de page — il n'existe pas dans les réglages magasin.
 */
import { useEffect, useState } from 'react';
import { fetchShopConfigCached } from '../../hooks/ecommerce/useShopOrdering';
import type { PlageHoraire } from '../../api/ecommerce.api';

const OLIVE = '#4a5c3a';
const TERRACOTTA = '#b45f2e';
const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const rangLundi = (j: number) => (j + 6) % 7;
const EMAIL = 'contact@foliesboutik.be';

/** « 0478/51.68.22 » → « tel:+32478516822 » (numéro belge). */
function lienTel(t: string): string {
  const chiffres = t.replace(/[^\d+]/g, '');
  if (chiffres.startsWith('+')) return `tel:${chiffres}`;
  if (chiffres.startsWith('00')) return `tel:+${chiffres.slice(2)}`;
  return `tel:+32${chiffres.replace(/^0/, '')}`;
}

/** « 14:00 » → « 14 h », « 07:30 » → « 7 h 30 ». */
function h(v?: string | null): string {
  if (!v) return '';
  const [hh, mm] = v.split(':');
  return mm && mm !== '00' ? `${Number(hh)} h ${mm}` : `${Number(hh)} h`;
}

function aujourdhuiBelge(): number {
  const j = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Brussels', weekday: 'short' }).format(new Date());
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(j);
}

function Horloge() {
  return (
    <svg width="26" height="26" viewBox="0 0 32 32" aria-hidden className="shrink-0">
      <circle cx="16" cy="16" r="12" fill="#f6efe2" stroke={OLIVE} strokeWidth="2.4" />
      <path d="M16 9 V16 L21 19" stroke={TERRACOTTA} strokeWidth="2.6" fill="none" strokeLinecap="round" />
    </svg>
  );
}

function Etal() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden className="shrink-0">
      <path d="M3 9 L5 4 H19 L21 9 Z" fill={TERRACOTTA} />
      <path d="M5 9 V20 M19 9 V20 M3 20 H21" stroke={OLIVE} strokeWidth="2" strokeLinecap="round" />
      <circle cx="10" cy="14" r="2" fill="#5e8a3a" />
      <circle cx="14.5" cy="14.5" r="2" fill="#c9412e" />
    </svg>
  );
}

export default function HorairesMagasin() {
  const [horaires, setHoraires] = useState<PlageHoraire[]>([]);
  const [adresse, setAdresse] = useState<string | null>(null);
  const [telephone, setTelephone] = useState<string | null>(null);

  useEffect(() => {
    let annule = false;
    fetchShopConfigCached()
      .then((c) => {
        if (annule) return;
        setHoraires(c.store?.opening_hours || []);
        setAdresse(c.store?.address ?? null);
        setTelephone(c.store?.phone ?? null);
      })
      .catch(() => {});
    return () => {
      annule = true;
    };
  }, []);

  if (horaires.length === 0) return null;

  const tries = [...horaires].sort((a, b) => rangLundi(a.day) - rangLundi(b.day) || a.from.localeCompare(b.from));
  const ouverts = new Set(tries.map((p) => p.day));
  const fermes = [1, 2, 3, 4, 5, 6, 0].filter((j) => !ouverts.has(j));
  const aujourdhui = aujourdhuiBelge();

  return (
    <div className="shop-sans rounded-2xl border border-[#4a5c3a]/20 bg-white p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-1">
        <Horloge />
        <h3 className="font-bold text-[16px]" style={{ color: TERRACOTTA }}>
          Horaires et contact
        </h3>
      </div>
      {adresse && <p className="text-[13px] text-[#5a5440] mb-3">{adresse}</p>}

      <ul className="divide-y divide-[#4a5c3a]/10">
        {tries.map((p) => {
          const estAujourdhui = p.day === aujourdhui;
          return (
            <li
              key={`${p.day}-${p.from}`}
              className={`flex items-center gap-3 py-2 px-2 -mx-2 rounded-lg ${estAujourdhui ? 'bg-[#4a5c3a]/[.08]' : ''}`}
            >
              <span className="w-[86px] shrink-0 text-[14.5px] font-bold capitalize text-[#3a3324]">
                {JOURS[p.day]}
                {estAujourdhui && (
                  <span className="block text-[10.5px] font-bold uppercase tracking-[.05em]" style={{ color: OLIVE }}>
                    aujourd’hui
                  </span>
                )}
              </span>
              {p.place ? (
                <span className="flex items-center gap-2 text-[14px] text-[#3a3324]">
                  <Etal />
                  <span>
                    <strong>{p.to ? `${h(p.from)} – ${h(p.to)}` : `dès ${h(p.from)}`}</strong>
                    <span className="block text-[12.5px] text-[#5a5440]">{p.place}</span>
                  </span>
                </span>
              ) : (
                <span className="text-[15px] font-semibold tabular-nums text-[#3a3324]">
                  {p.to ? `${h(p.from)} – ${h(p.to)}` : p.note || `dès ${h(p.from)}`}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {fermes.length > 0 && (
        <p className="mt-2 text-[12.5px] text-[#6b6550]">
          Fermé {fermes.map((j) => JOURS[j]).join(', ').replace(/, ([^,]*)$/, ' et $1')}.
        </p>
      )}

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {telephone && (
          <a
            href={lienTel(telephone)}
            className="flex min-h-[48px] items-center gap-3 rounded-xl border-2 border-[#4a5c3a]/30 px-3 active:bg-[#eef2e8]"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden className="shrink-0">
              <path
                d="M6.6 10.8 C8 13.6 10.4 16 13.2 17.4 L15.4 15.2 C15.7 14.9 16.1 14.8 16.5 15 C17.6 15.4 18.8 15.6 20 15.6 C20.6 15.6 21 16 21 16.6 V20 C21 20.6 20.6 21 20 21 C10.6 21 3 13.4 3 4 C3 3.4 3.4 3 4 3 H7.5 C8.1 3 8.5 3.4 8.5 4 C8.5 5.2 8.7 6.4 9.1 7.5 C9.2 7.9 9.1 8.3 8.8 8.6 Z"
                fill={OLIVE}
              />
            </svg>
            <span>
              <span className="block text-[11.5px] font-bold uppercase tracking-[.05em] text-[#6b6550]">Téléphone</span>
              <span className="text-[15px] font-semibold text-[#3a3324]">{telephone}</span>
            </span>
          </a>
        )}
        <a
          href={`mailto:${EMAIL}`}
          className="flex min-h-[48px] items-center gap-3 rounded-xl border-2 border-[#4a5c3a]/30 px-3 active:bg-[#eef2e8]"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden className="shrink-0">
            <rect x="3" y="5" width="18" height="14" rx="2.5" fill="#f6efe2" stroke={OLIVE} strokeWidth="2" />
            <path d="M4 7 L12 13 L20 7" stroke={TERRACOTTA} strokeWidth="2" fill="none" strokeLinejoin="round" />
          </svg>
          <span className="min-w-0">
            <span className="block text-[11.5px] font-bold uppercase tracking-[.05em] text-[#6b6550]">Email</span>
            <span className="block truncate text-[15px] font-semibold text-[#3a3324]">{EMAIL}</span>
          </span>
        </a>
      </div>
    </div>
  );
}
